import { PersonaId } from '../personas';

export type MockReview = {
  author: string;
  rating: number;
  snippet: string;
};

export type MockBooking = {
  when: string;
  client: string;
  service: string;
};

export type MockService = {
  title: string;
  priceNote?: string;
  price: string;
};

export type PersonaMock = {
  avatarEmoji: string;
  displayName: string;
  professionLine: string;
  joinedLine: string;
  rating: number;
  reviewCount: number;
  services: MockService[];
  totalServices: number;
  planName: string;
  renewsLine: string;
  bannerText: string;
  reviews: MockReview[];
  upcomingBookings: MockBooking[];
  avgBookingPrice: number;
  defaultExtraClients: number;
};

const DEFAULT_EXTRA_CLIENTS = 4;

const FIVE = 5;
const FOUR_NINE = 4.9;

const DEFAULT_BANNER =
  'Share your Kudable URL in your bio — every review becomes proof that travels with you.';

const CV_BANNER =
  'Your reviews can be used on your CV, for promotion conversations, or to land your next role.';

export const PERSONA_MOCKS: Record<PersonaId, PersonaMock> = {
  content_creator: {
    avatarEmoji: '🎤',
    displayName: 'Naledi M.',
    professionLine: 'Content Creator · Promoter',
    joinedLine: 'Joined 1 year ago',
    rating: FOUR_NINE,
    reviewCount: 76,
    services: [
      { title: 'Sponsored Post · IG + TikTok', priceNote: 'per post', price: '$800 – $1,500' },
      { title: 'UGC Video Pack · 5 clips', priceNote: 'per pack', price: '$1,200 – $2,400' },
      { title: 'Brand Ambassador · 3 months', priceNote: 'per quarter', price: '$3,500 – $6,000' },
    ],
    totalServices: 5,
    planName: 'Pro+',
    renewsLine: 'Renews 21 Aug 2026',
    bannerText: DEFAULT_BANNER,
    reviews: [
      { author: 'Kayla B. · @lumebrand', rating: FIVE, snippet: 'Drove 4× the engagement of our last sponsored partner. Easy to brief, on time.' },
      { author: 'Marcus T. · @atlasco', rating: FIVE, snippet: 'Edits were on-brand and the analytics speak for themselves. Booking the next quarter.' },
      { author: 'Studio M.', rating: FIVE, snippet: 'Reliable, responsive, and the audience genuinely trusts her recommendations.' },
    ],
    upcomingBookings: [
      { when: 'Today · 10:00', client: 'Kayla B.', service: 'IG sponsored post' },
      { when: 'Fri · 14:00', client: 'Marcus T.', service: 'UGC shoot · 5 clips' },
    ],
    avgBookingPrice: 1100,
    defaultExtraClients: DEFAULT_EXTRA_CLIENTS,
  },
  cleaner: {
    avatarEmoji: '🧽',
    displayName: 'Sarah J.',
    professionLine: 'House Cleaner',
    joinedLine: 'Joined 2 years ago',
    rating: FOUR_NINE,
    reviewCount: 127,
    services: [
      { title: 'Standard Weekly Clean', priceNote: 'per visit', price: '$120 – $180' },
      { title: 'Bi-Weekly Clean', priceNote: 'per visit', price: '$140 – $210' },
      { title: 'Deep Clean', priceNote: 'one-off', price: '$280 – $520' },
    ],
    totalServices: 10,
    planName: 'Pro+',
    renewsLine: 'Renews 13 Jun 2026',
    bannerText: CV_BANNER,
    reviews: [
      { author: 'Rachel M.', rating: FIVE, snippet: 'Spotless every time. My go-to for years.' },
      { author: 'Daniel K.', rating: FIVE, snippet: 'Reliable, thorough, and trustworthy.' },
      { author: 'Priya S.', rating: FIVE, snippet: 'Best cleaner in Austin. Period.' },
    ],
    upcomingBookings: [
      { when: 'Today · 10:00', client: 'Rachel M.', service: 'Deep clean' },
      { when: 'Tomorrow · 14:00', client: 'James O.', service: 'Standard clean' },
    ],
    avgBookingPrice: 150,
    defaultExtraClients: DEFAULT_EXTRA_CLIENTS,
  },
  electrician: {
    avatarEmoji: '⚡',
    displayName: 'Michael C.',
    professionLine: 'Master Electrician',
    joinedLine: 'Joined 3 years ago',
    rating: FOUR_NINE,
    reviewCount: 86,
    services: [
      { title: 'Panel Upgrade · 200A', priceNote: 'per job', price: '$1,200 – $1,800' },
      { title: 'Outlet Install', priceNote: 'per outlet', price: '$95 – $140' },
      { title: 'Emergency Callout', priceNote: 'after hours', price: '$220 – $320' },
    ],
    totalServices: 8,
    planName: 'Pro+',
    renewsLine: 'Renews 22 May 2026',
    bannerText: CV_BANNER,
    reviews: [
      { author: 'Tom B.', rating: FIVE, snippet: 'Fast, clean, professional. Quoted on-site, done next day.' },
      { author: 'Sandra L.', rating: FIVE, snippet: 'Solved a wiring issue three other guys missed.' },
      { author: 'Marcus T.', rating: FIVE, snippet: 'Honest pricing. No surprises on the invoice.' },
    ],
    upcomingBookings: [
      { when: 'Today · 09:00', client: 'Sandra L.', service: 'Outlet install · 4' },
      { when: 'Fri · 13:00', client: 'Marcus T.', service: 'Panel inspection' },
    ],
    avgBookingPrice: 1500,
    defaultExtraClients: DEFAULT_EXTRA_CLIENTS,
  },
  painter: {
    avatarEmoji: '🎨',
    displayName: 'Chris W.',
    professionLine: 'House Painter',
    joinedLine: 'Joined 1 year ago',
    rating: 4.8,
    reviewCount: 64,
    services: [
      { title: 'Interior Repaint · 2 rooms', priceNote: 'per job', price: '$580 – $820' },
      { title: 'Exterior Repaint', priceNote: 'per side', price: '$1,400 – $2,200' },
      { title: 'Trim & Doors', priceNote: 'per visit', price: '$220 – $380' },
    ],
    totalServices: 7,
    planName: 'Pro+',
    renewsLine: 'Renews 8 Aug 2026',
    bannerText: CV_BANNER,
    reviews: [
      { author: 'Helen P.', rating: FIVE, snippet: 'Clean lines, finished a day early. Highly recommend.' },
      { author: 'Greg D.', rating: FIVE, snippet: 'Quoted in 10 minutes, started same week.' },
      { author: 'Megan A.', rating: FIVE, snippet: 'Took care of trim and edges — perfect finish.' },
    ],
    upcomingBookings: [
      { when: 'Mon · 08:00', client: 'Helen P.', service: 'Living room repaint' },
      { when: 'Wed · 09:00', client: 'Greg D.', service: 'Exterior · trim' },
    ],
    avgBookingPrice: 700,
    defaultExtraClients: DEFAULT_EXTRA_CLIENTS,
  },
  hair_stylist: {
    avatarEmoji: '💇',
    displayName: 'Jenny M.',
    professionLine: 'Hair, Nails & Beauty',
    joinedLine: 'Joined 2 years ago',
    rating: FOUR_NINE,
    reviewCount: 214,
    services: [
      { title: 'Cut + Colour · full', priceNote: 'per visit', price: '$120 – $180' },
      { title: 'Balayage', priceNote: 'per visit', price: '$220 – $320' },
      { title: 'Wash + Blowout', priceNote: 'per visit', price: '$60 – $90' },
    ],
    totalServices: 9,
    planName: 'Pro+',
    renewsLine: 'Renews 13 Jun 2026',
    bannerText: DEFAULT_BANNER,
    reviews: [
      { author: 'Sofia R.', rating: FIVE, snippet: 'My hair has never looked better. Booked for next month already.' },
      { author: 'Alyssa B.', rating: FIVE, snippet: 'Got my balayage exactly right on the first try.' },
      { author: 'Vanessa C.', rating: FIVE, snippet: 'Vibe is amazing. Reviews are 100% real.' },
    ],
    upcomingBookings: [
      { when: 'Today · 11:30', client: 'Sofia R.', service: 'Cut + colour' },
      { when: 'Today · 15:00', client: 'Alyssa B.', service: 'Balayage' },
    ],
    avgBookingPrice: 150,
    defaultExtraClients: DEFAULT_EXTRA_CLIENTS,
  },
  daycare: {
    avatarEmoji: '🧸',
    displayName: 'Emily T.',
    professionLine: 'Daycare · Childcare',
    joinedLine: 'Joined 4 years ago',
    rating: 5.0,
    reviewCount: 53,
    services: [
      { title: 'Full Day · 1 child', priceNote: 'per day', price: '$65 – $90' },
      { title: 'Half Day · 1 child', priceNote: 'per day', price: '$38 – $55' },
      { title: 'After-School Care', priceNote: 'per afternoon', price: '$28 – $42' },
    ],
    totalServices: 5,
    planName: 'Pro+',
    renewsLine: 'Renews 5 Apr 2026',
    bannerText: CV_BANNER,
    reviews: [
      { author: 'Karen H.', rating: FIVE, snippet: 'My daughter loves it here. Trustworthy and warm.' },
      { author: 'Joel S.', rating: FIVE, snippet: 'Daily updates, photos, peace of mind. Worth every cent.' },
      { author: 'Nadia P.', rating: FIVE, snippet: 'Felt safe leaving our toddler from day one.' },
    ],
    upcomingBookings: [
      { when: 'Tomorrow · 07:30', client: 'Karen H.', service: 'Full day' },
      { when: 'Tomorrow · 08:00', client: 'Joel S.', service: 'Half day' },
    ],
    avgBookingPrice: 75,
    defaultExtraClients: DEFAULT_EXTRA_CLIENTS,
  },
  healthcare: {
    avatarEmoji: '🩺',
    displayName: 'Grace O.',
    professionLine: 'Home Care Nurse',
    joinedLine: 'Joined 3 years ago',
    rating: 5.0,
    reviewCount: 96,
    services: [
      { title: 'In-Home Nursing Visit', priceNote: 'per visit', price: '$90 – $140' },
      { title: 'Post-Op Recovery Care', priceNote: 'per day', price: '$180 – $260' },
      { title: 'Elderly Day Care', priceNote: 'per day', price: '$150 – $220' },
    ],
    totalServices: 6,
    planName: 'Pro+',
    renewsLine: 'Renews 11 May 2026',
    bannerText: CV_BANNER,
    reviews: [
      { author: 'The Bennett Family', rating: FIVE, snippet: 'Cared for my father like her own. We finally slept at night.' },
      { author: 'Nora P.', rating: FIVE, snippet: 'Gentle, punctual, and deeply knowledgeable. A blessing.' },
      { author: 'Dr. Ellis · referral', rating: FIVE, snippet: 'I refer my post-op patients to her without hesitation.' },
    ],
    upcomingBookings: [
      { when: 'Today · 08:00', client: 'The Bennett Family', service: 'Morning care visit' },
      { when: 'Today · 15:00', client: 'Nora P.', service: 'Post-op check-in' },
    ],
    avgBookingPrice: 150,
    defaultExtraClients: DEFAULT_EXTRA_CLIENTS,
  },
  plumber: {
    avatarEmoji: '🔧',
    displayName: 'David T.',
    professionLine: 'Plumber',
    joinedLine: 'Joined 3 years ago',
    rating: FOUR_NINE,
    reviewCount: 142,
    services: [
      { title: 'Emergency Drain Unblock', priceNote: 'callout', price: '$180 – $260' },
      { title: 'Water Heater Install', priceNote: 'per job', price: '$650 – $1,100' },
      { title: 'Leak Repair', priceNote: 'per visit', price: '$120 – $220' },
    ],
    totalServices: 9,
    planName: 'Pro+',
    renewsLine: 'Renews 19 Jul 2026',
    bannerText: CV_BANNER,
    reviews: [
      { author: 'Maria L.', rating: FIVE, snippet: 'Showed up in 40 minutes on a Sunday. Saved us.' },
      { author: 'Peter G.', rating: FIVE, snippet: 'Honest, fair price, fixed it right the first time.' },
      { author: 'Lisa O.', rating: FIVE, snippet: 'My new go-to plumber. Will recommend forever.' },
    ],
    upcomingBookings: [
      { when: 'Today · 10:30', client: 'Maria L.', service: 'Drain unblock' },
      { when: 'Today · 14:00', client: 'Peter G.', service: 'Water heater install' },
    ],
    avgBookingPrice: 220,
    defaultExtraClients: DEFAULT_EXTRA_CLIENTS,
  },
  mechanic: {
    avatarEmoji: '🔩',
    displayName: 'Matt D.',
    professionLine: 'Mechanic',
    joinedLine: 'Joined 5 years ago',
    rating: 4.8,
    reviewCount: 178,
    services: [
      { title: 'Full Service + Diagnostic', priceNote: 'per car', price: '$220 – $360' },
      { title: 'Brake Replacement', priceNote: 'per axle', price: '$280 – $480' },
      { title: 'Engine Diagnostic', priceNote: 'one-off', price: '$95 – $140' },
    ],
    totalServices: 11,
    planName: 'Pro+',
    renewsLine: 'Renews 30 Sep 2026',
    bannerText: CV_BANNER,
    reviews: [
      { author: 'Diane R.', rating: FIVE, snippet: 'Quoted a fair price, beat the dealer by half.' },
      { author: 'Robert K.', rating: FIVE, snippet: 'Catches things the dealer would never check.' },
      { author: 'Steph M.', rating: FIVE, snippet: 'Trust him with my whole family’s cars.' },
    ],
    upcomingBookings: [
      { when: 'Today · 08:00', client: 'Diane R.', service: 'Full service' },
      { when: 'Today · 11:00', client: 'Robert K.', service: 'Brake replace' },
    ],
    avgBookingPrice: 290,
    defaultExtraClients: DEFAULT_EXTRA_CLIENTS,
  },
  mover: {
    avatarEmoji: '🚚',
    displayName: 'Ryan H.',
    professionLine: 'Mover · Delivery',
    joinedLine: 'Joined 2 years ago',
    rating: 4.8,
    reviewCount: 88,
    services: [
      { title: 'Apartment Move · studio–2BR', priceNote: 'per move', price: '$320 – $680' },
      { title: 'Furniture Delivery · single item', priceNote: 'per trip', price: '$90 – $160' },
      { title: 'Packing + Loading Crew', priceNote: 'per hour', price: '$110 – $170' },
    ],
    totalServices: 5,
    planName: 'Pro+',
    renewsLine: 'Renews 2 Jul 2026',
    bannerText: CV_BANNER,
    reviews: [
      { author: 'Chelsea M.', rating: FIVE, snippet: 'Moved my 1-bed in three hours, nothing scratched. Crew was great.' },
      { author: 'Devon K.', rating: FIVE, snippet: 'On time, fair flat quote, careful with the heavy stuff.' },
      { author: 'Ana R.', rating: FIVE, snippet: 'Booked a same-week couch delivery. Painless.' },
    ],
    upcomingBookings: [
      { when: 'Sat · 08:00', client: 'Chelsea M.', service: 'Apartment move · 2BR' },
      { when: 'Sun · 11:00', client: 'Devon K.', service: 'Furniture delivery' },
    ],
    avgBookingPrice: 420,
    defaultExtraClients: DEFAULT_EXTRA_CLIENTS,
  },
  gardener: {
    avatarEmoji: '🌿',
    displayName: 'Andrew M.',
    professionLine: 'Gardener · Landscaper',
    joinedLine: 'Joined 2 years ago',
    rating: 4.8,
    reviewCount: 71,
    services: [
      { title: 'Lawn + Hedges', priceNote: 'weekly', price: '$75 – $110' },
      { title: 'Garden Redesign', priceNote: 'per project', price: '$420 – $1,200' },
      { title: 'One-off Cleanup', priceNote: 'per visit', price: '$140 – $220' },
    ],
    totalServices: 6,
    planName: 'Pro+',
    renewsLine: 'Renews 14 Aug 2026',
    bannerText: CV_BANNER,
    reviews: [
      { author: 'Linda W.', rating: FIVE, snippet: 'My yard has never looked this good. Reliable as clockwork.' },
      { author: 'Owen P.', rating: FIVE, snippet: 'Re-designed our front garden — neighbours keep asking.' },
      { author: 'Hannah T.', rating: FIVE, snippet: 'Shows up rain or shine. Worth every dollar.' },
    ],
    upcomingBookings: [
      { when: 'Today · 09:00', client: 'Linda W.', service: 'Weekly maintenance' },
      { when: 'Thu · 10:00', client: 'Owen P.', service: 'Hedge trim' },
    ],
    avgBookingPrice: 90,
    defaultExtraClients: DEFAULT_EXTRA_CLIENTS,
  },
  tutor: {
    avatarEmoji: '📚',
    displayName: 'Megan W.',
    professionLine: 'Math Tutor',
    joinedLine: 'Joined 3 years ago',
    rating: 5.0,
    reviewCount: 92,
    services: [
      { title: 'Algebra II · 60 min', priceNote: 'per session', price: '$55 – $75' },
      { title: 'Calculus · Exam Prep', priceNote: 'per session', price: '$70 – $95' },
      { title: 'Study Plan Review', priceNote: 'one-off', price: '$40 – $60' },
    ],
    totalServices: 4,
    planName: 'Pro+',
    renewsLine: 'Renews 12 Jan 2027',
    bannerText: CV_BANNER,
    reviews: [
      { author: 'Janet F.', rating: FIVE, snippet: 'My son went from a C+ to an A in one semester.' },
      { author: 'Eric B.', rating: FIVE, snippet: 'Patient, sharp, and actually fun for the kids.' },
      { author: 'Tara S.', rating: FIVE, snippet: 'Worth every cent. Booking again next term.' },
    ],
    upcomingBookings: [
      { when: 'Today · 16:00', client: 'Janet F.', service: 'Algebra II · weekly' },
      { when: 'Today · 17:30', client: 'Eric B.', service: 'Calculus · prep' },
    ],
    avgBookingPrice: 65,
    defaultExtraClients: DEFAULT_EXTRA_CLIENTS,
  },
  caterer: {
    avatarEmoji: '🍽',
    displayName: 'Ashley W.',
    professionLine: 'Caterer · Private Chef',
    joinedLine: 'Joined 2 years ago',
    rating: FOUR_NINE,
    reviewCount: 58,
    services: [
      { title: 'Wedding · 80 guests', priceNote: 'per event', price: '$3,800 – $5,200' },
      { title: 'Corporate Lunch · 30', priceNote: 'per event', price: '$680 – $1,100' },
      { title: 'Private Dinner · 8', priceNote: 'per event', price: '$420 – $720' },
    ],
    totalServices: 8,
    planName: 'Pro+',
    renewsLine: 'Renews 28 May 2026',
    bannerText: DEFAULT_BANNER,
    reviews: [
      { author: 'Sasha N.', rating: FIVE, snippet: 'Every guest asked who the caterer was. Stunning food.' },
      { author: 'Vega Inc.', rating: FIVE, snippet: 'Catered our retreat — flawless, on time, beautiful.' },
      { author: 'Renee O.', rating: FIVE, snippet: 'My 40th was a hit. Booked again for the anniversary.' },
    ],
    upcomingBookings: [
      { when: 'Sat · 14:00', client: 'Sasha N.', service: 'Wedding · 80 guests' },
      { when: 'Sun · 12:00', client: 'Vega Inc.', service: 'Corporate brunch' },
    ],
    avgBookingPrice: 4500,
    defaultExtraClients: DEFAULT_EXTRA_CLIENTS,
  },
  trainer: {
    avatarEmoji: '💪',
    displayName: 'Amanda R.',
    professionLine: 'Personal Trainer',
    joinedLine: 'Joined 2 years ago',
    rating: FOUR_NINE,
    reviewCount: 134,
    services: [
      { title: '1-on-1 Session · 60 min', priceNote: 'per session', price: '$80 – $120' },
      { title: 'Strength Block · 8 weeks', priceNote: 'per programme', price: '$520 – $760' },
      { title: 'HIIT Group · 45 min', priceNote: 'per class', price: '$28 – $42' },
    ],
    totalServices: 6,
    planName: 'Pro+',
    renewsLine: 'Renews 17 Apr 2026',
    bannerText: DEFAULT_BANNER,
    reviews: [
      { author: 'Jess P.', rating: FIVE, snippet: 'Lost 18kg with her plan. Changed my life.' },
      { author: 'Marcus B.', rating: FIVE, snippet: 'Got me in the best shape of my 30s.' },
      { author: 'Tasha L.', rating: FIVE, snippet: 'Sessions fly by. She actually gets results.' },
    ],
    upcomingBookings: [
      { when: 'Today · 06:30', client: 'Jess P.', service: '1-on-1 · strength' },
      { when: 'Today · 18:00', client: 'Marcus B.', service: '1-on-1 · HIIT' },
    ],
    avgBookingPrice: 100,
    defaultExtraClients: DEFAULT_EXTRA_CLIENTS,
  },
  photographer: {
    avatarEmoji: '📸',
    displayName: 'Jessica B.',
    professionLine: 'Wedding Photographer',
    joinedLine: 'Joined 3 years ago',
    rating: 5.0,
    reviewCount: 47,
    services: [
      { title: 'Full-day Wedding', priceNote: 'per event', price: '$2,400 – $3,800' },
      { title: 'Engagement Shoot', priceNote: 'per session', price: '$420 – $680' },
      { title: 'Family Portraits', priceNote: 'per session', price: '$280 – $480' },
    ],
    totalServices: 5,
    planName: 'Pro+',
    renewsLine: 'Renews 9 Sep 2026',
    bannerText: DEFAULT_BANNER,
    reviews: [
      { author: 'Tara + Sam', rating: FIVE, snippet: 'Captured every moment. Photos blew us away.' },
      { author: 'Olivia + Mike', rating: FIVE, snippet: 'Calm, fast, and the edits arrived in two weeks.' },
      { author: 'Priya + Dev', rating: FIVE, snippet: 'Best decision we made for the wedding.' },
    ],
    upcomingBookings: [
      { when: 'Sat · 11:00', client: 'Tara + Sam', service: 'Full-day wedding' },
      { when: 'Next Fri · 16:00', client: 'Olivia + Mike', service: 'Engagement shoot' },
    ],
    avgBookingPrice: 3100,
    defaultExtraClients: DEFAULT_EXTRA_CLIENTS,
  },
  designer: {
    avatarEmoji: '🎨',
    displayName: 'Lauren C.',
    professionLine: 'Graphic Designer',
    joinedLine: 'Joined 1 year ago',
    rating: 4.8,
    reviewCount: 38,
    services: [
      { title: 'Brand Identity · logo + guide', priceNote: 'per project', price: '$1,200 – $1,800' },
      { title: 'Logo Concepts · 3', priceNote: 'per project', price: '$480 – $720' },
      { title: 'Social Pack · 10 posts', priceNote: 'per pack', price: '$320 – $520' },
    ],
    totalServices: 6,
    planName: 'Pro+',
    renewsLine: 'Renews 14 Jul 2026',
    bannerText: DEFAULT_BANNER,
    reviews: [
      { author: 'Anna · @luxbakery', rating: FIVE, snippet: 'Brand kit was exactly the vibe. Got it in a week.' },
      { author: 'Liam · GreenLeaf Co', rating: FIVE, snippet: 'Three concepts, all good. Hard to pick.' },
      { author: 'Sofia · @studio4', rating: FIVE, snippet: 'Pro process, clean files, fast turnaround.' },
    ],
    upcomingBookings: [
      { when: 'Mon · 10:00', client: 'Anna · luxbakery', service: 'Brand kit · v2' },
      { when: 'Wed · 14:00', client: 'Liam · GreenLeaf', service: 'Logo concepts' },
    ],
    avgBookingPrice: 1500,
    defaultExtraClients: DEFAULT_EXTRA_CLIENTS,
  },
  developer: {
    avatarEmoji: '💻',
    displayName: 'Aaron F.',
    professionLine: 'Developer',
    joinedLine: 'Joined 1 year ago',
    rating: 5.0,
    reviewCount: 29,
    services: [
      { title: 'Landing Page · React + Stripe', priceNote: 'per project', price: '$2,200 – $3,200' },
      { title: 'API Integration · 2 weeks', priceNote: 'per project', price: '$3,800 – $5,800' },
      { title: 'Discovery Call · 30 min', priceNote: 'one-off', price: 'Free' },
    ],
    totalServices: 5,
    planName: 'Pro+',
    renewsLine: 'Renews 3 Nov 2026',
    bannerText: DEFAULT_BANNER,
    reviews: [
      { author: 'Lena · founder', rating: FIVE, snippet: 'Shipped in 5 days. Stripe + email already wired.' },
      { author: 'Mark · Tactile Co', rating: FIVE, snippet: 'Clean code, daily updates, on budget.' },
      { author: 'Priya · 4thWall', rating: FIVE, snippet: 'Best contractor I’ve ever worked with.' },
    ],
    upcomingBookings: [
      { when: 'Today · 11:00', client: 'Lena (intro call)', service: 'Discovery · 30 min' },
      { when: 'Thu · 15:00', client: 'Mark · Tactile', service: 'Sprint review' },
    ],
    avgBookingPrice: 2800,
    defaultExtraClients: DEFAULT_EXTRA_CLIENTS,
  },
  videographer: {
    avatarEmoji: '🎥',
    displayName: 'Marcus L.',
    professionLine: 'Videographer',
    joinedLine: 'Joined 2 years ago',
    rating: FOUR_NINE,
    reviewCount: 41,
    services: [
      { title: 'Brand Film · 60s edit', priceNote: 'per project', price: '$1,600 – $2,400' },
      { title: 'Event Coverage · 4 hours', priceNote: 'per event', price: '$1,200 – $1,800' },
      { title: 'Reels Pack · 5 edits', priceNote: 'per pack', price: '$640 – $920' },
    ],
    totalServices: 5,
    planName: 'Pro+',
    renewsLine: 'Renews 26 Jun 2026',
    bannerText: DEFAULT_BANNER,
    reviews: [
      { author: 'Hana · @northgear', rating: FIVE, snippet: 'Film hit 1M views on IG. Booked the next two.' },
      { author: 'Devon · founder', rating: FIVE, snippet: 'Pro lighting, pro sound, painless shoot day.' },
      { author: 'Talia · @flora', rating: FIVE, snippet: 'Edit was ready in 4 days. Stunning work.' },
    ],
    upcomingBookings: [
      { when: 'Sat · 09:00', client: 'Hana · northgear', service: 'Shoot day · brand' },
      { when: 'Mon · 13:00', client: 'Devon (review)', service: 'Edit · final cut' },
    ],
    avgBookingPrice: 2000,
    defaultExtraClients: DEFAULT_EXTRA_CLIENTS,
  },
  dj: {
    avatarEmoji: '🎧',
    displayName: 'Brandon L.',
    professionLine: 'Wedding DJ',
    joinedLine: 'Joined 4 years ago',
    rating: 5.0,
    reviewCount: 76,
    services: [
      { title: 'Wedding Set · 4 hours', priceNote: 'per event', price: '$1,400 – $2,000' },
      { title: 'Corporate Set · 3 hours', priceNote: 'per event', price: '$1,100 – $1,600' },
      { title: 'Add-on · MC services', priceNote: 'per event', price: '$280 – $420' },
    ],
    totalServices: 4,
    planName: 'Pro+',
    renewsLine: 'Renews 7 Oct 2026',
    bannerText: DEFAULT_BANNER,
    reviews: [
      { author: 'Megan + Jay', rating: FIVE, snippet: 'Dancefloor was packed all night. Best DJ in TN.' },
      { author: 'Ari + Sam', rating: FIVE, snippet: 'Read the room perfectly. 10/10.' },
      { author: 'LaunchHQ', rating: FIVE, snippet: 'Booked him for the launch party. People stayed for hours.' },
    ],
    upcomingBookings: [
      { when: 'Sat · 18:00', client: 'Megan + Jay', service: 'Wedding · 4hr set' },
      { when: 'Next Fri · 20:00', client: 'LaunchHQ', service: 'Launch party' },
    ],
    avgBookingPrice: 1700,
    defaultExtraClients: DEFAULT_EXTRA_CLIENTS,
  },
  legal: {
    avatarEmoji: '⚖️',
    displayName: 'Daniel R.',
    professionLine: 'Tax Consultant',
    joinedLine: 'Joined 5 years ago',
    rating: FOUR_NINE,
    reviewCount: 62,
    services: [
      { title: 'Annual Tax Filing · self-employed', priceNote: 'per year', price: '$380 – $560' },
      { title: 'Quarterly Review', priceNote: 'per quarter', price: '$180 – $280' },
      { title: 'Discovery Call · 30 min', priceNote: 'one-off', price: 'Free' },
    ],
    totalServices: 4,
    planName: 'Pro+',
    renewsLine: 'Renews 31 Mar 2026',
    bannerText: CV_BANNER,
    reviews: [
      { author: 'Owen · founder', rating: FIVE, snippet: 'Saved me 12k in my first year working with him.' },
      { author: 'Hannah · freelance', rating: FIVE, snippet: 'Clear, fast, and made tax season painless.' },
      { author: 'Jordan · agency', rating: FIVE, snippet: 'Knows the freelancer game inside out.' },
    ],
    upcomingBookings: [
      { when: 'Today · 10:00', client: 'Owen (intro)', service: 'Discovery call' },
      { when: 'Fri · 14:00', client: 'Hannah · freelance', service: 'Filing review' },
    ],
    avgBookingPrice: 470,
    defaultExtraClients: DEFAULT_EXTRA_CLIENTS,
  },
  other: {
    avatarEmoji: '🛠',
    displayName: 'Alex P.',
    professionLine: 'Service Pro',
    joinedLine: 'Joined 1 year ago',
    rating: FOUR_NINE,
    reviewCount: 64,
    services: [
      { title: 'Standard Booking', priceNote: 'per visit', price: '$95 – $160' },
      { title: 'Extended Visit', priceNote: 'per visit', price: '$160 – $260' },
      { title: 'Quick Consult · 20 min', priceNote: 'one-off', price: '$40 – $60' },
    ],
    totalServices: 5,
    planName: 'Pro+',
    renewsLine: 'Renews 1 Aug 2026',
    bannerText: CV_BANNER,
    reviews: [
      { author: 'Sam K.', rating: FIVE, snippet: 'Showed up on time, did the work right.' },
      { author: 'Mira L.', rating: FIVE, snippet: 'Easy to book, easy to recommend.' },
      { author: 'Drew B.', rating: FIVE, snippet: 'Will book again without hesitation.' },
    ],
    upcomingBookings: [
      { when: 'Today · 10:00', client: 'Sam K.', service: 'Standard booking' },
      { when: 'Tomorrow · 14:00', client: 'Mira L.', service: 'Follow-up' },
    ],
    avgBookingPrice: 130,
    defaultExtraClients: DEFAULT_EXTRA_CLIENTS,
  },
};
